// ============================================================================
// /subcontractors — Subcontractor CARD GRID landing (2026-07-10 REV 2).
// Ben rejected the read-only drawer-over-table; this replaces the flat table
// with a responsive two-column card grid (one column on mobile) modeled on the
// facility-card pattern. Each card shows ONLY basics — go-by, legal, location,
// active-assignment count, capability tags, a status dot and an "N missing"
// corner flag. Clicking a card ROUTES to the full detail page
// (/subcontractors/:providerId) where the tabs + edit slide-over live.
//
// Toolbar: search across name / legal / owner / location / capability + raw
// service_type; capability filter chips (All + each present capability); a
// "needs attention" toggle (preserves Antonela's missing-docs triage that the
// old W9/COI filter pills gave her); a "Show inactive" toggle. Merged subs are
// excluded at the query (Task 3). Onboarding stays a page action.
// ============================================================================
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  BarChart3,
  MapPin,
  MessageSquare,
  Repeat,
  Search,
} from 'lucide-react';
import { EmptyState, ErrorState, Skeleton, useToast } from '../../shared/components';
import { supabase } from '../../shared/supabase';
import { useSubcontractors, type SubcontractorRow } from './hooks/use-onboarding-data';
import { CAPABILITY_ORDER, type Capability } from '../subcontractors/capabilities';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { useWorkingSubInsurance } from './hooks/use-working-sub-insurance';
import { useSubcontractorTiers } from './hooks/use-subcontractor-tiers';
import { useSubBench } from './hooks/use-sub-bench';
import { setSubTier1 } from '../subcontractors/hub/actions';
import { AtRiskView, TierRosterView, applyTierFilters, atRisk, type TierRow } from './tier-roster-parts';
import { BenchView, benchByMarket } from './bench-parts';
import {
  AccountManagerView,
  NO_MANAGER,
  byAccountManager,
  type AmRow,
  type AmSortKey,
} from './am-view-parts';
import { useCanAccessSourcing } from '../../shared/use-can-access-sourcing';
import { marketOptions as marketNames, useMarkets } from '../../shared/markets';
import {
  INSURANCE_SCOPE_ORDER,
  UninsuredSubsView,
  scopeFilter,
  subLabel,
  type InsuranceScope,
} from './uninsured-subs-parts';

// The board's two views. ⛔ The tab lives in the URL, not useState (TCSD-0082):
// the HR and Finance dashboard cards link straight at the insurance list, and a
// useState tab is unreachable from a link. The LANDING tab is represented by NO
// parameter so the plain /subcontractors URL stays canonical.
// Tiers joined them on 2026-09-11. Ben asked for the tier roster HERE, beside
// the other two — it is a subcontractor view, not a sourcing one.
type BoardTab = 'subs' | 'insurance' | 'tiers' | 'bench' | 'risk' | 'managers';

function parseBoardTab(raw: string | null): BoardTab {
  if (raw === 'insurance') return 'insurance';
  if (raw === 'tiers') return 'tiers';
  if (raw === 'bench') return 'bench';
  if (raw === 'risk') return 'risk';
  if (raw === 'managers') return 'managers';
  return 'subs';
}

// Payment info present == onboarding `direct_deposit` checklist item complete
// (link-only; no PII). Same rule the detail page + old drawer use.
function directDepositComplete(raw: Record<string, unknown> | null): boolean {
  if (!raw) return false;
  const docs = (raw as { documents?: unknown }).documents;
  if (!Array.isArray(docs)) return false;
  return docs.some(
    (it) =>
      it &&
      typeof it === 'object' &&
      (it as { key?: string }).key === 'direct_deposit' &&
      (it as { status?: string }).status === 'complete',
  );
}

// Missing basics for the corner flag — mirrors the detail-page Missing chips.
function missingItems(r: SubcontractorRow): string[] {
  const out: string[] = [];
  if (!r.w9_link) out.push('W9');
  if (r.coi_light === 'red') out.push('COI');
  if (!r.phone) out.push('Phone');
  if (!directDepositComplete(r.rawChecklistJson)) out.push('Payment info');
  if (r.active_assignments === 0) out.push('Active assignment');
  return out;
}

/** Since item #683 this board is the Subcontractors hub's Compliance tab, unchanged in its
 *  sections and their order (Antonela's review follows). The hub owns the title and the one
 *  "+ Add" button, so this renders neither; its "Onboard new sub" button is retired with the
 *  second create path (one door, TCSD-0368). */
export default function SubcontractorsPage(_props: { embedded?: boolean } = {}) {
  const { rows, loading, error } = useSubcontractors();
  const [params, setParams] = useSearchParams();
  const boardTab = parseBoardTab(params.get('tab'));
  const setBoardTab = (t: BoardTab) => {
    const next = new URLSearchParams(params);
    t === 'subs' ? next.delete('tab') : next.set('tab', t);
    setParams(next, { replace: true });
  };
  const [showInactive, setShowInactive] = useState(false);
  const [needsAttention, setNeedsAttention] = useState(false);
  const [query, setQuery] = useState('');
  const [capFilter, setCapFilter] = useState<Capability | 'all'>('all');
  const toast = useToast();

  // Alias map (provider_id -> aliases) so search resolves nicknames matching
  // the dedup aliases. One lightweight fetch.
  const [aliasMap, setAliasMap] = useState<Record<string, string[]>>({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('entity_aliases')
        .select('entity_id, alias')
        .eq('entity_type', 'service_provider');
      if (cancelled) return;
      const m: Record<string, string[]> = {};
      for (const a of (data ?? []) as { entity_id: string; alias: string }[]) {
        (m[a.entity_id] ??= []).push(a.alias);
      }
      setAliasMap(m);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Base set after the inactive toggle (Merged already excluded at the query).
  const base = useMemo(
    () => (showInactive ? rows : rows.filter((r) => r.provider_status !== 'Inactive')),
    [rows, showInactive],
  );

  // Capabilities present across the base set — drives the filter chips.
  const presentCaps = useMemo(() => {
    const set = new Set<Capability>();
    for (const r of base) for (const c of r.capabilities) set.add(c);
    return CAPABILITY_ORDER.filter((c) => set.has(c));
  }, [base]);

  const filtered = useMemo(() => {
    let list = base;
    if (capFilter !== 'all') list = list.filter((r) => r.capabilities.includes(capFilter));
    if (needsAttention) list = list.filter((r) => missingItems(r).length > 0);
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) => {
      const hay = [
        r.company,
        r.legal_business_name,
        r.owner_name,
        r.market_region,
        r.email,
        ...r.capabilities,
        ...r.raw_service_types,
        ...(aliasMap[r.provider_id] ?? []),
      ];
      return hay.some((v) => v && v.toLowerCase().includes(q));
    });
  }, [base, capFilter, needsAttention, query, aliasMap]);

  const inactiveCount = useMemo(
    () => rows.filter((r) => r.provider_status === 'Inactive').length,
    [rows],
  );

  // ----- Insurance view ------------------------------------------------------
  // Scope is fixed at the query: Active providers with active_assignments > 0.
  // The default chip is the exposure — subs with live work and NO valid COI —
  // and that same count is what the HR and Finance dashboard cards show, so the
  // number a person clicks is the number of rows they land on.
  const {
    rows: insuranceAll,
    loading: insuranceLoading,
    error: insuranceError,
  } = useWorkingSubInsurance();
  const [insuranceScope, setInsuranceScope] = useState<InsuranceScope>('no_valid_coi');
  const insuranceCounts = useMemo(
    () =>
      Object.fromEntries(
        INSURANCE_SCOPE_ORDER.map((s) => [s, scopeFilter(insuranceAll, s).length]),
      ) as Record<InsuranceScope, number>,
    [insuranceAll],
  );
  const insuranceRows = useMemo(
    () => scopeFilter(insuranceAll, insuranceScope),
    [insuranceAll, insuranceScope],
  );
  const insuranceAssignments = useMemo(
    () => insuranceRows.reduce((n, r) => n + r.active_assignments, 0),
    [insuranceRows],
  );
  const insuranceAlertCount = insuranceCounts.no_valid_coi;

  // ----- Tiers tab -------------------------------------------------------
  // ⛔ The tier arithmetic is the view's. Nothing here recomputes a threshold.
  const {
    rows: tierAll,
    loading: tiersLoading,
    error: tiersError,
    refetch: refetchTiers,
  } = useSubcontractorTiers();
  const [tierMarket, setTierMarket] = useState('');
  const [tierAm, setTierAm] = useState('');
  const [tierBusy, setTierBusy] = useState<string | null>(null);
  const tierRows = useMemo(
    () => applyTierFilters(tierAll, tierMarket, tierAm),
    [tierAll, tierMarket, tierAm],
  );

  // ----- Bench tab -------------------------------------------------------
  // ⛔ RLS gates sourcing_prospects, so for anyone outside the sourcing list the
  // query returns zero rows with NO error — which would render as "the bench is
  // empty" rather than "you cannot see this". So the TAB is gated, not the data.
  const { canAccess: canSeeBench } = useCanAccessSourcing();
  const { rows: benchAll, loading: benchLoading, error: benchError } = useSubBench();
  const { markets } = useMarkets();
  const [benchMarket, setBenchMarket] = useState('');
  const marketList = useMemo(() => marketNames(markets), [markets]);
  // Subs actually working each market today — the denominator that makes a
  // bench count mean something.
  const workingByMarket = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of tierAll) {
      if (r.lead_market) m[r.lead_market] = (m[r.lead_market] ?? 0) + 1;
    }
    return m;
  }, [tierAll]);
  const benchCover = useMemo(
    () => benchByMarket(benchAll, marketList, workingByMarket),
    [benchAll, marketList, workingByMarket],
  );

  const atRiskCount = useMemo(() => atRisk(tierAll).length, [tierAll]);

  // ----- By account manager ----------------------------------------------
  // Bench cover per manager needs the bench, which is gated: for anyone who
  // cannot see sourcing the bench reads as empty, so the column would say
  // "nobody ready" to the whole company. Pass an empty bench and hide the
  // column's meaning rather than assert something false.
  // Opens on buildings lost, most first — Ben, 2026-09-14. Every column still
  // sorts on click; this is only the resting state.
  const [amSort, setAmSort] = useState<AmSortKey>('lost60');
  const [amDesc, setAmDesc] = useState(true);
  const amAll = useMemo(
    () => byAccountManager(tierAll, canSeeBench ? benchAll : []),
    [tierAll, benchAll, canSeeBench],
  );
  const amRows = useMemo(() => amAll.filter((r) => r.eid !== NO_MANAGER), [amAll]);
  const amUnassigned = useMemo<AmRow | null>(
    () => amAll.find((r) => r.eid === NO_MANAGER) ?? null,
    [amAll],
  );
  const onAmSort = (k: AmSortKey) => {
    if (k === amSort) setAmDesc((d) => !d);
    else {
      setAmSort(k);
      setAmDesc(k !== 'name');
    }
  };

  async function setTier1(row: TierRow, next: boolean) {
    setTierBusy(row.provider_id);
    try {
      await setSubTier1(row.provider_id, next);
      toast.success(
        next ? `${row.display_name} is now Tier 1` : `${row.display_name} is no longer Tier 1`,
      );
      refetchTiers();
    } catch (e) {
      toast.error(
        next ? 'Could not make that sub Tier 1' : 'Could not remove Tier 1',
        { description: rpcErrorMessage(e, '') },
      );
    } finally {
      setTierBusy(null);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-tcs-text-muted">{`${filtered.length} of ${base.length} shown · tap a card for detail`}</p>

      {/* Two views on one board: the sub cards, and the insurance list the
          dashboard cards point at. */}
      <div role="tablist" aria-label="Subcontractor board views" className="flex gap-1 border-b border-tcs-border">
        <BoardTabButton
          label="Subcontractors"
          active={boardTab === 'subs'}
          onClick={() => setBoardTab('subs')}
        />
        <BoardTabButton
          label={`Insurance${insuranceAlertCount > 0 ? ` (${insuranceAlertCount})` : ''}`}
          active={boardTab === 'insurance'}
          onClick={() => setBoardTab('insurance')}
          alert={insuranceAlertCount > 0}
        />
        <BoardTabButton
          label="Tiers"
          active={boardTab === 'tiers'}
          onClick={() => setBoardTab('tiers')}
        />
        {canSeeBench && (
          <BoardTabButton
            label={`Bench${benchAll.length > 0 ? ` (${benchAll.length})` : ''}`}
            active={boardTab === 'bench'}
            onClick={() => setBoardTab('bench')}
          />
        )}
        <BoardTabButton
          label={`At risk${atRiskCount > 0 ? ` (${atRiskCount})` : ''}`}
          active={boardTab === 'risk'}
          onClick={() => setBoardTab('risk')}
          alert={atRiskCount > 0}
        />
        <BoardTabButton
          label="By manager"
          active={boardTab === 'managers'}
          onClick={() => setBoardTab('managers')}
        />
      </div>

      {boardTab === 'managers' ? (
        tiersError ? (
          <ErrorState title="Couldn't load the tier roster" body={tiersError} />
        ) : tiersLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : (
          <AccountManagerView
            rows={amRows}
            unassigned={amUnassigned}
            sortKey={amSort}
            sortDesc={amDesc}
            onSort={onAmSort}
            renderMarkets={(r) => (r.markets.length ? r.markets.join(', ') : '—')}
          />
        )
      ) : boardTab === 'risk' ? (
        tiersError ? (
          <ErrorState title="Couldn't load the tier roster" body={tiersError} />
        ) : tiersLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : (
          <AtRiskView
            rows={tierAll}
            renderName={(r) => (
              <Link to={`/subcontractors/${r.provider_id}`} className="hover:underline">
                {r.display_name}
              </Link>
            )}
          />
        )
      ) : boardTab === 'bench' ? (
        !canSeeBench ? (
          <EmptyState
            title="The bench isn't visible to your account yet"
            body="Sourcing is in a restricted rollout, and the bench is part of it. Ask the COO if you think you should have access."
          />
        ) : benchError ? (
          <ErrorState title="Couldn't load the bench" body={benchError} />
        ) : benchLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : (
          <BenchView
            rows={benchAll}
            cover={benchCover}
            market={benchMarket}
            onMarket={setBenchMarket}
            markets={marketList}
            renderName={(r) => (
              <Link to={`/people/subcontractors/${r.provider_id}`} className="hover:underline">
                {r.display_name}
              </Link>
            )}
          />
        )
      ) : boardTab === 'tiers' ? (
        tiersError ? (
          <ErrorState title="Couldn't load the tier roster" body={tiersError} />
        ) : tiersLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : (
          <TierRosterView
            rows={tierRows}
            allRows={tierAll}
            market={tierMarket}
            am={tierAm}
            onMarket={setTierMarket}
            onAm={setTierAm}
            busyProviderId={tierBusy}
            onSetTier1={setTier1}
            renderName={(r) => (
              <Link to={`/subcontractors/${r.provider_id}`} className="hover:underline">
                {r.display_name}
              </Link>
            )}
          />
        )
      ) : boardTab === 'insurance' ? (
        insuranceError ? (
          <ErrorState title="Couldn't load insurance status" body={insuranceError} />
        ) : insuranceLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : (
          <UninsuredSubsView
            rows={insuranceRows}
            scope={insuranceScope}
            onScope={setInsuranceScope}
            counts={insuranceCounts}
            totalAssignments={insuranceAssignments}
            renderName={(r) => (
              <Link to={`/subcontractors/${r.provider_id}`} className="hover:underline">
                {subLabel(r)}
              </Link>
            )}
          />
        )
      ) : (
        <>
      {/* Consolidated Subcontractors IA (#10): one home for sub actions. */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Link to="/subcontractors/directory" className="inline-flex items-center gap-1.5 rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 font-medium text-tcs-text hover:bg-tcs-surface">
          <BarChart3 className="h-3.5 w-3.5" /> Directory — revenue, margin &amp; feedback
        </Link>
        <Link to="/sub-replacement" className="inline-flex items-center gap-1.5 rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-tcs-text hover:bg-tcs-surface">
          <Repeat className="h-3.5 w-3.5" /> Sub replacement
        </Link>
        <Link to="/project-work" className="inline-flex items-center gap-1.5 rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-tcs-text hover:bg-tcs-surface">
          <MessageSquare className="h-3.5 w-3.5" /> Projects & Billing Comms
        </Link>
      </div>

      {/* Toolbar: search + capability chips + attention + inactive */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-tcs-text-muted" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, legal, owner, location, capability…"
              className="w-80 max-w-full rounded-md border border-tcs-border bg-tcs-bg py-1.5 pl-8 pr-3 text-sm"
            />
          </div>
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-tcs-text-secondary">
            <input
              type="checkbox"
              checked={needsAttention}
              onChange={(e) => setNeedsAttention(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-tcs-border"
            />
            Needs attention
          </label>
          <label className="ml-auto inline-flex cursor-pointer items-center gap-1.5 text-xs text-tcs-text-secondary">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-tcs-border"
            />
            Show inactive{inactiveCount ? ` (${inactiveCount})` : ''}
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <CapChip label="All" active={capFilter === 'all'} onClick={() => setCapFilter('all')} />
          {presentCaps.map((c) => (
            <CapChip key={c} label={c} active={capFilter === c} onClick={() => setCapFilter(c)} />
          ))}
        </div>
      </div>

      {error && <ErrorState title="Couldn't load subcontractors" body={error} />}

      {loading ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState title="No subcontractors match" body="Try clearing the search or capability filter." />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {filtered.map((r) => (
            <SubCard key={r.provider_id} row={r} />
          ))}
        </div>
      )}

        </>
      )}

    </div>
  );
}

function BoardTabButton({
  label,
  active,
  onClick,
  alert,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  alert?: boolean;
}) {
  return (
    <button
      role="tab"
      type="button"
      aria-selected={active}
      onClick={onClick}
      className={[
        '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors',
        active
          ? 'border-tcs-text font-medium text-tcs-text'
          : 'border-transparent text-tcs-text-secondary hover:text-tcs-text',
        !active && alert ? 'text-st-danger-fg' : '',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

// ----- Card ----------------------------------------------------------------

const STATUS_DOT: Record<string, string> = {
  Active: 'bg-st-ok-solid',
  Inactive: 'bg-st-neutral',
  Pending: 'bg-st-warn-solid',
  Onboarding: 'bg-st-warn-solid',
  FLAG: 'bg-st-danger-solid',
};

function SubCard({ row }: { row: SubcontractorRow }) {
  const missing = missingItems(row);
  const location = row.market_region ?? '—';
  return (
    <Link
      to={`/subcontractors/${row.provider_id}`}
      className="relative block rounded-lg border border-tcs-border bg-tcs-raised p-4 transition-colors hover:border-tcs-text/30 hover:bg-tcs-surface"
    >
      {/* Missing corner flag */}
      {missing.length > 0 && (
        <span
          title={`Missing: ${missing.join(', ')}`}
          className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full border border-st-danger-border bg-st-danger-bg px-2 py-0.5 text-[11px] font-medium text-st-danger-fg"
        >
          <AlertTriangle className="h-3 w-3" /> {missing.length} missing
        </span>
      )}

      <div className="flex items-start gap-2 pr-20">
        <span className={`mt-1.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${STATUS_DOT[row.provider_status ?? ''] ?? 'bg-tcs-border-strong'}`} />
        <div className="min-w-0">
          <div className="truncate text-base font-semibold text-tcs-text">{row.company ?? row.provider_id}</div>
          {row.legal_business_name && row.legal_business_name !== row.company && (
            <div className="truncate text-[11px] text-tcs-text-muted">Legal: {row.legal_business_name}</div>
          )}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-tcs-text-secondary">
        <span className="inline-flex items-center gap-1">
          <MapPin className="h-3.5 w-3.5 text-tcs-text-muted" /> {location}
        </span>
        <span className="tabular-nums">
          {row.active_assignments} active {row.active_assignments === 1 ? 'assignment' : 'assignments'}
        </span>
      </div>

      {row.capabilities.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {row.capabilities.map((c) => (
            <span key={c} className="rounded-full border border-tcs-border bg-tcs-bg px-2 py-0.5 text-[11px] font-medium text-tcs-text-secondary">
              {c}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}

function CapChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs transition-colors ${
        active
          ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
          : 'border-tcs-border bg-tcs-bg text-tcs-text-secondary hover:bg-tcs-surface'
      }`}
    >
      {label}
    </button>
  );
}
